import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import {
    GenericObject,
    checkEmail,
    checkPhoneNumber,
    common,
    formatPhoneValue,
    getApplicationId,
    getIsCallCenter,
    getIsWebsite,
    nameMaxLength,
    prepareCitiesList,
    prepareCountriesList,
    questionMaxLength,
    surnameMaxLength,
    getIsSalesForce,
} from '@fnol/library/src/lib';
import { PhoneInputField, StepWrapper, StepperControls, StyledCardSection } from '@fnol/library/src/components';
import {
    constValues,
    insuranceGroup,
    legalPersonType,
    objectTypeOptions,
    personType,
    personValidationFields,
    translations,
} from '../../lib/consts';
import { useValidateIndividual, useValidateLegalEntity } from '../../lib/customHooks';
import Individual, { IndividualCleanup } from '../Individual/Individual';
import {
    getCities,
    getCountries,
    getPersonRoles,
    getPersonValidation,
    getUserData,
    getInsuredPerson,
} from '../../rpc/actions';
import LegalIndividualSelector from '../LegalIndividualSelector';
import LegalEntity, { LegalEntityCleanup } from '../LegalEntity/LegalEntity';
import { useApi } from '@fnol/library/src/hooks';
import {
    preparePersonRoles,
    preparePersonValidationData,
    scrollToFirstError,
    prepareInsuredPerson,
} from '../../lib/helpers';

type SubmitterDetailScreenProps = {
    navigator: GenericObject[];
    values: GenericObject[];
};

interface FormData {
    countries: Array<GenericObject>;
    cities: Array<GenericObject>;
    submitterType: legalPersonType;
    submitterRole: string;
    damagedObject: string;
    submitterLegalEntityID: string;
    submitterLegalEntityCountry: string | number;
    submitterLegalEntityMyCountry: string;
    submitterLegalEntityEmail: string;
    submitterLegalEntityContactName: string;
    submitterLegalEntityContactLastName: string;
    submitterLegalEntityContactEmail: string;
    submitterLegalEntityContactPhone: string;
    VATFormatError: boolean;
    validationError: string;
    legalEntityIdFieldFormatError: boolean;
    submitterIndividualUCN: string;
    submitterIndividualEmail: string;
    submitterIndividualPhone: string;
    submitterIndividualPersonRoles: Array<GenericObject>;
    submitterLegalEntityPersonRoles: Array<GenericObject>;
    submitterIndividualPersonRole: string;
    submitterLegalEntityPersonRole: string;
    submitterPhoneError: string;
    submitterEmailError: string;
    submitterEmailContactError: string;
    submitterUcnError: string;
    policyNumber: string;
    groupInsuranceMethod: number;
    insuredPerson: GenericObject;
    streetNames: Array<GenericObject>;
    prefillSubmitterOwner: boolean;
}

const SubmitterDetailScreen = ({ navigator }: SubmitterDetailScreenProps) => {
    const [currentStep, { goForward, goBack }] = navigator;
    const { values, errors, touched, setErrors, setTouched, setFieldValue } = useFormikContext<FormData>();

    const claimRequestVisible = getIsWebsite(getApplicationId()) || getIsCallCenter(getApplicationId());
    const submitterIndividualValidation = useValidateIndividual(personType.submitter);
    const submitterLegalEntityValidation = useValidateLegalEntity(personType.submitter);

    const [preparedCountries, setPreparedCountries] = useState<GenericObject>();

    const dataForValidation: GenericObject = {};

    const saveCountries = (res: GenericObject) => {
        setFieldValue('countries', prepareCountriesList(res.data));
    };
    const saveCities = (res: GenericObject) => {
        setFieldValue('cities', prepareCitiesList(res.data));
    };
    const savePersonValidation = (res: GenericObject) => {
        setTouched(
            {
                submitterIndividualPhone: true,
                submitterIndividualEmail: true,
                submitterIndividualUCN: true,
                submitterLegalEntityEmail: true,
                submitterLegalEntityContactEmail: true,
                submitterLegalEntityContactPhone: true,
            },
            false,
        );

        const existingErrors: GenericObject = {};

        if (values.submitterType === legalPersonType.individual) {
            preparePersonValidationData(res.data, personValidationFields.phone)[0] &&
            formatPhoneValue(values.submitterIndividualPhone) !== ''
                ? (existingErrors.submitterIndividualPhone = preparePersonValidationData(
                      res.data,
                      personValidationFields.phone,
                  )[0])
                : null;
            preparePersonValidationData(res.data, personValidationFields.email)[0]
                ? (existingErrors.submitterIndividualEmail = preparePersonValidationData(
                      res.data,
                      personValidationFields.email,
                  )[0])
                : null;
            preparePersonValidationData(res.data, personValidationFields.ucn)[0]
                ? (existingErrors.submitterIndividualUCN = preparePersonValidationData(
                      res.data,
                      personValidationFields.ucn,
                  )[0])
                : null;
        }

        if (values.submitterType === legalPersonType.legal) {
            preparePersonValidationData(res.data, personValidationFields.phone)[0]
                ? (existingErrors.submitterLegalEntityContactPhone = preparePersonValidationData(
                      res.data,
                      personValidationFields.phone,
                  )[0])
                : null;
            preparePersonValidationData(res.data, personValidationFields.email)[0]
                ? (existingErrors.submitterLegalEntityEmail = preparePersonValidationData(
                      res.data,
                      personValidationFields.email,
                  )[0])
                : null;
            preparePersonValidationData(res.data, personValidationFields.emailContact)[0]
                ? (existingErrors.submitterLegalEntityContactEmail = preparePersonValidationData(
                      res.data,
                      personValidationFields.emailContact,
                  )[0])
                : null;
        }

        if (Object.keys(existingErrors).length === 0) {
            goForward();
        } else {
            setTimeout(() => {
                setErrors(existingErrors);
            });
        }
    };

    const savePersonRoles = (res: GenericObject) => {
        setFieldValue('submitterIndividualPersonRoles', preparePersonRoles(res.data, legalPersonType.individual));
        setFieldValue('submitterLegalEntityPersonRoles', preparePersonRoles(res.data, legalPersonType.legal));
        setFieldValue('personRoles', res.data);
    };

    const saveInsuredPerson = (res: GenericObject) => {
        setFieldValue('insuredPerson', prepareInsuredPerson(res.data));
        if (typeof values.submitterIndividualPersonRole === 'number' && values.submitterIndividualPersonRole === 1) {
            if (values.prefillSubmitterOwner === true) {
                IndividualCleanup(personType.submitter, setFieldValue);
                prefillSubmitterOwnerStandard(prepareInsuredPerson(res.data));
                setFieldValue('prefillSubmitterOwner', false);
            }
        }
    };

    const { response: countries, fetchMethod: fetchCountries } = useApi(() => getCountries(), saveCountries);
    const { response: cities, fetchMethod: fetchCities } = useApi(() => getCities(), saveCities);
    const { response: personRoles, fetchMethod: fetchPersonRoles } = useApi(() => getPersonRoles(), savePersonRoles);
    const { response: personValidation, fetchMethod: fetchPersonValidation } = useApi(
        () => getPersonValidation(dataForValidation),
        savePersonValidation,
    );
    const { response: insuredPerson, fetchMethod: fetchInsuredPerson } = useApi(
        () => getInsuredPerson({ brojPolise: values.policyNumber }),
        saveInsuredPerson,
    );

    const handleGoBack = (): void => {
        goBack();
    };

    const handleNext = () => {
        setTouched(
            {
                damagedObject: true,
                submitterType: true,
                submitterLegalEntityContactName: true,
                submitterLegalEntityContactLastName: true,
                submitterLegalEntityContactEmail: true,
                submitterLegalEntityContactPhone: true,
                submitterIndividualPersonRole: true,
                submitterLegalEntityPersonRole: true,
                ...submitterIndividualValidation.touched,
                ...submitterLegalEntityValidation.touched,
            },
            false,
        );
        let existingErrors: GenericObject = {};
        let valid = true;

        if (!values.damagedObject) {
            existingErrors.damagedObject = translations.validationMessages.REQUIRED_FIELD;
            valid = false;
        }

        if (!!claimRequestVisible) {
            if (values.submitterType === legalPersonType.initialValue) {
                existingErrors.submitterType = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
        }

        if (values.submitterType === legalPersonType.individual) {
            if (!values.submitterIndividualPersonRole) {
                existingErrors.submitterIndividualPersonRole = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            existingErrors = {
                ...submitterIndividualValidation.errors,
                ...existingErrors,
            };
            if (!submitterIndividualValidation.isValid) valid = false;
        }

        if (values.submitterType === legalPersonType.legal) {
            if (!values.submitterLegalEntityPersonRole) {
                existingErrors.submitterLegalEntityPersonRole = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.submitterLegalEntityContactName) {
                existingErrors.submitterLegalEntityContactName = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.submitterLegalEntityContactLastName) {
                existingErrors.submitterLegalEntityContactLastName = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }

            if (!values.submitterLegalEntityContactEmail) {
                existingErrors.submitterLegalEntityContactEmail = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (!checkEmail(values.submitterLegalEntityContactEmail)) {
                existingErrors.submitterLegalEntityContactEmail = translations.validationMessages.EMAIL_ADDRESS_FORMAT;
                valid = false;
            }

            if (!values.submitterLegalEntityContactPhone) {
                existingErrors.submitterLegalEntityContactPhone = translations.validationMessages.REQUIRED_FIELD;
                valid = false;
            }
            if (!checkPhoneNumber(values.submitterLegalEntityContactPhone)) {
                existingErrors.submitterLegalEntityContactPhone =
                    translations.validationMessages.PHONE_NUMBER_FORMAT_SERBIAN;
                valid = false;
            }

            existingErrors = {
                ...submitterLegalEntityValidation.errors,
                ...existingErrors,
            };
            if (!submitterLegalEntityValidation.isValid) valid = false;
        }

        setTimeout(() => {
            setErrors(existingErrors);
        });
        scrollToFirstError(existingErrors);

        if (valid) {
            if (values.submitterType === legalPersonType.individual) {
                dataForValidation[personValidationFields.ucn] = values.submitterIndividualUCN;
                dataForValidation[personValidationFields.email] = values.submitterIndividualEmail;
                dataForValidation[personValidationFields.phone] = formatPhoneValue(values.submitterIndividualPhone);
                fetchPersonValidation();
            } else if (values.submitterType === legalPersonType.legal) {
                dataForValidation[personValidationFields.email] = values.submitterLegalEntityEmail;
                dataForValidation[personValidationFields.emailContact] = values.submitterLegalEntityContactEmail;
                dataForValidation[personValidationFields.phone] = formatPhoneValue(
                    values.submitterLegalEntityContactPhone,
                );
                fetchPersonValidation();
            }
        }
    };

    const handleChangeLegalPersonType = () => {
        setFieldValue('submitterLegalEntityContactName', '');
        setFieldValue('submitterLegalEntityContactLastName', '');
        setFieldValue('submitterLegalEntityContactEmail', '');
        setFieldValue('submitterLegalEntityContactPhone', constValues.dialCodeSerbia);
        setFieldValue('submitterLegalEntityPersonRole', '');
        setFieldValue('submitterIndividualPersonRole', '');

        LegalEntityCleanup(personType.submitter, setFieldValue);
        IndividualCleanup(personType.submitter, setFieldValue);
        setFieldValue('prefillSubmitterOwner', true);
    };

    useEffect(() => {
        Array.isArray(values.countries) && setPreparedCountries(values.countries);
    }, [countries]);

    const selectedLegalEntityCountry = preparedCountries?.find(
        (item: any) => item.id === parseInt(values.submitterLegalEntityCountry as string),
    );
    values.submitterLegalEntityMyCountry = selectedLegalEntityCountry?.text;

    useEffect(() => {
        if (!claimRequestVisible) {
            getUserData().then((response) => {
                response.data.ime && setFieldValue('submitterIndividualName', response.data.ime);
                response.data.prezime && setFieldValue('submitterIndividualLastName', response.data.prezime);
                response.data.email && setFieldValue('submitterIndividualEmail', response.data.email);
                response.data.telefon && setFieldValue('submitterIndividualPhone', response.data.telefon);
                response.data.jmbg && setFieldValue('submitterIndividualUCN', response.data.jmbg);
            });

            setFieldValue('submitterType', legalPersonType.individual);
        }
        fetchCountries();
        fetchCities();
        fetchPersonRoles();
        if (values.groupInsuranceMethod === insuranceGroup.policyNumber && getIsSalesForce(getApplicationId())) {
            fetchInsuredPerson();
        }
    }, []);

    useEffect(() => {
        if (values.groupInsuranceMethod === insuranceGroup.policyNumber && getIsSalesForce(getApplicationId())) {
            if (
                typeof values.submitterIndividualPersonRole === 'number' &&
                values.submitterIndividualPersonRole === 1
            ) {
                const streetNames = values.streetNames;
                if (values.insuredPerson.address !== '') {
                    const newStreetName = {
                        id: '-1',
                        value: values.insuredPerson.address,
                        label: values.insuredPerson.address,
                    };
                    streetNames.push(newStreetName);
                    setFieldValue('streetNames', streetNames);
                    setFieldValue('submitterIndividualAddress', newStreetName);
                }
            }
        }
    }, [values.streetNames]);

    const prefillSubmitterOwnerStandard = (insuredPerson: GenericObject) => {
        setFieldValue('submitterIndividualName', insuredPerson.name ? insuredPerson.name : '');
        setFieldValue('submitterIndividualLastName', insuredPerson.lastName ? insuredPerson.lastName : '');
        // If country ID is not 0, set it to submitter
        if (insuredPerson.countryId && insuredPerson.countryId.toString(10) !== '0') {
            setFieldValue('submitterIndividualCountry', insuredPerson.countryId.toString(10));
        }
        if (
            insuredPerson.countryId &&
            insuredPerson.countryId.toString(10) === constValues.countrySerbiaId.toString(10)
        ) {
            // If country is Serbia, set ucn
            setFieldValue('submitterIndividualUCN', insuredPerson.ucn ? insuredPerson.ucn : '');
            const cityId = insuredPerson.cityId ? insuredPerson.cityId : '';
            const city = values.cities.find((s) => s.id == insuredPerson.cityId);
            const cityName = city ? city.label : '';
            const cityCode = insuredPerson.cityCode ? insuredPerson.cityCode : '';
            const postalCode = insuredPerson.postalCode ? insuredPerson.postalCode : '';
            const cityInSerbia = {
                id: cityId,
                value: `${cityId}/${cityName}`,
                label: cityName,
                zipCode: postalCode,
                code: cityCode,
            };
            if (cityId !== '' && cityName !== '') {
                setFieldValue('submitterIndividualCityInSerbia', cityInSerbia);
            }
        } else {
            setFieldValue('submitterIndividualCity', insuredPerson.cityName ? insuredPerson.cityName : '');
            setFieldValue('submitterIndividualAddress', insuredPerson.address);
        }
        setFieldValue(
            'submitterIndividualAddressNumber',
            insuredPerson.addressNumber ? insuredPerson.addressNumber : '',
        );
        setFieldValue('submitterIndividualEmail', insuredPerson.email ? insuredPerson.email : '');
        setFieldValue('submitterIndividualPhone', insuredPerson.phone ? insuredPerson.phone : '');
    };

    const handleChangeIndividualPersonRole = (e: SelectChangeEvent<string>) => {
        setFieldValue('submitterIndividualPersonRole', e.target.value);
        setFieldValue('ownerType', '');
        setFieldValue('ownerPick', '');
        IndividualCleanup(personType.owner, setFieldValue);
        if (values.groupInsuranceMethod === insuranceGroup.policyNumber && getIsSalesForce(getApplicationId())) {
            if (typeof e.target.value === 'number' && e.target.value === 1 && values.prefillSubmitterOwner === true) {
                prefillSubmitterOwnerStandard(values.insuredPerson);
                setFieldValue('prefillSubmitterOwner', false);
            } else {
                IndividualCleanup(personType.submitter, setFieldValue);
                setFieldValue('prefillSubmitterOwner', true);
            }
        }
    };

    const handleChangeLegalEntityPersonRole = (e: SelectChangeEvent<string>) => {
        setFieldValue('submitterLegalEntityPersonRole', e.target.value);
        setFieldValue('ownerType', '');
        setFieldValue('ownerPick', '');
        LegalEntityCleanup(personType.owner, setFieldValue);
    };

    const handleChangeDamagedObject = (e: { target: { value: any } }): any => {
        setFieldValue('damagedObject', e.target.value);
        setFieldValue('addressObjectSameAsSubmitter', '');
        setFieldValue('objectCity', '');
        setFieldValue('objectStreet', '');
        setFieldValue('objectStreetNumber', '');
    };

    return (
        <StepWrapper>
            <StyledCardSection>
                <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                    {translations.CLAIM_SUBMITTER}
                </Typography>

                <FormControl error={!!(touched.damagedObject && errors.damagedObject)} required>
                    <InputLabel>{translations.DAMAGE_OBJECT}</InputLabel>
                    <Select
                        id="damagedObject"
                        name="damagedObject"
                        value={values.damagedObject || ''}
                        label={translations.DAMAGE_OBJECT}
                        onChange={handleChangeDamagedObject}
                    >
                        {objectTypeOptions.map((option: GenericObject) => {
                            return (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.value}
                                </MenuItem>
                            );
                        })}
                    </Select>
                    {touched.damagedObject && errors.damagedObject ? (
                        <FormHelperText error={true}>
                            <>{errors.damagedObject}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>

                {claimRequestVisible && (
                    <>
                        <Typography style={{ fontWeight: 'bold', fontSize: '18px' }} variant="caption">
                            {translations.CHOOSE_SUBMITTER_TYPE + ' *'}
                        </Typography>

                        <LegalIndividualSelector
                            idIndividual="submitterIndividual"
                            idLegal="submitterLegal"
                            selectorType={personType.submitter}
                            onChange={handleChangeLegalPersonType}
                        ></LegalIndividualSelector>
                    </>
                )}

                {values.submitterType === legalPersonType.individual && (
                    <>
                        <FormControl
                            error={!!(touched.submitterIndividualPersonRole && errors.submitterIndividualPersonRole)}
                            required
                        >
                            <InputLabel>{translations.personalInfo.PERSON_ROLE}</InputLabel>
                            <Select
                                id="submitterIndividualPersonRole"
                                name="submitterIndividualPersonRole"
                                value={values.submitterIndividualPersonRole || ''}
                                label={translations.personalInfo.PERSON_ROLE}
                                onChange={handleChangeIndividualPersonRole}
                            >
                                {values.submitterIndividualPersonRoles?.map((option: GenericObject) => {
                                    return (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.text}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {touched.submitterIndividualPersonRole && errors.submitterIndividualPersonRole ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterIndividualPersonRole}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <Individual individualType={personType.submitter} />
                    </>
                )}
                {values.submitterType === legalPersonType.legal && (
                    <>
                        <FormControl
                            error={!!(touched.submitterLegalEntityPersonRole && errors.submitterLegalEntityPersonRole)}
                            required
                        >
                            <InputLabel>{translations.personalInfo.PERSON_ROLE}</InputLabel>
                            <Select
                                id="submitterLegalEntityPersonRole"
                                name="submitterLegalEntityPersonRole"
                                value={values.submitterLegalEntityPersonRole || ''}
                                label={translations.personalInfo.PERSON_ROLE}
                                onChange={handleChangeLegalEntityPersonRole}
                            >
                                {values.submitterLegalEntityPersonRoles?.map((option: GenericObject) => {
                                    return (
                                        <MenuItem key={option.id} value={option.id}>
                                            {option.text}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {touched.submitterLegalEntityPersonRole && errors.submitterLegalEntityPersonRole ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterLegalEntityPersonRole}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <LegalEntity legalEntityType={personType.submitter}></LegalEntity>

                        <Typography
                            style={{ fontWeight: 'bold', fontSize: '18px', paddingTop: '16px' }}
                            variant="caption"
                        >
                            {translations.legalInfo.CONTACT_INFO}
                        </Typography>
                        <FormControl>
                            <Field
                                name="submitterLegalEntityContactName"
                                id="submitterLegalEntityContactName"
                                label={translations.legalInfo.NAME}
                                as={TextField}
                                inputProps={{
                                    maxLength: nameMaxLength,
                                }}
                                error={
                                    !!(
                                        touched.submitterLegalEntityContactName &&
                                        errors.submitterLegalEntityContactName
                                    )
                                }
                                required
                            />
                            {touched.submitterLegalEntityContactName && errors.submitterLegalEntityContactName ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterLegalEntityContactName}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <Field
                                name="submitterLegalEntityContactLastName"
                                id="submitterLegalEntityContactLastName"
                                label={translations.legalInfo.LAST_NAME}
                                as={TextField}
                                inputProps={{
                                    maxLength: surnameMaxLength,
                                }}
                                error={
                                    !!(
                                        touched.submitterLegalEntityContactLastName &&
                                        errors.submitterLegalEntityContactLastName
                                    )
                                }
                                required
                            />
                            {touched.submitterLegalEntityContactLastName &&
                            errors.submitterLegalEntityContactLastName ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterLegalEntityContactLastName}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                        <FormControl>
                            <PhoneInputField
                                name="submitterLegalEntityContactPhone"
                                id="submitterLegalEntityContactPhone"
                                specialLabel={translations.legalInfo.MOBILE}
                            />
                        </FormControl>
                        <FormControl>
                            <Field
                                name="submitterLegalEntityContactEmail"
                                id="submitterLegalEntityContactEmail"
                                label={translations.legalInfo.CONTACT_EMAIL}
                                type="email"
                                as={TextField}
                                inputProps={{
                                    maxLength: questionMaxLength,
                                }}
                                error={
                                    !!(
                                        touched.submitterLegalEntityContactEmail &&
                                        errors.submitterLegalEntityContactEmail
                                    )
                                }
                                required
                            />
                            {touched.submitterLegalEntityContactEmail && errors.submitterLegalEntityContactEmail ? (
                                <FormHelperText error={true}>
                                    <>{errors.submitterLegalEntityContactEmail}</>
                                </FormHelperText>
                            ) : null}
                        </FormControl>
                    </>
                )}
            </StyledCardSection>

            <StepperControls
                onPrevious={handleGoBack}
                onPreviousLabel={common.PREVIOUS}
                onNext={handleNext}
                onNextLabel={common.NEXT}
                idNext="submitterScreenNext"
                idPrevious="submitterScreenPrevious"
            />
        </StepWrapper>
    );
};

export default SubmitterDetailScreen;
